class SlideshowBlaze extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.autoplayTimer = null;
    this.isAutoplay = false;
  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.slides = this.querySelectorAll('.slideshow-blaze__slide');
    this.boxes = this.querySelectorAll('.slideshow-blaze__box');
    this.dotsContainer = this.querySelector('.slideshow-blaze__dots');
    this.slideCount = this.slides.length;

    if (this.slideCount < 1) return;

    if (this.slideCount > 1) {
      this.buildDots();
      this.buildArrows();
      this.bindSwipe();
      this.bindKeyboard();
    }

    // Banner link clicks
    this.slides.forEach((slide) => {
      const bannerLink = slide.querySelector('.slideshow-blaze__banner-link');
      if (bannerLink) {
        slide.style.cursor = 'pointer';
        slide.addEventListener('click', (e) => {
          if (e.target.closest('a.slideshow-blaze__overlay-btn') || e.target.closest('button')) return;
          const href = bannerLink.getAttribute('href');
          if (href) window.location.href = href;
        });
      }
    });

    // Set first slide
    this.goTo(0, true);

    // Autoplay
    if (this.dataset.autorotate === 'true' && this.slideCount > 1) {
      this.isAutoplay = true;
      this.observeVisibility();
    }
  }

  buildDots() {
    if (!this.dotsContainer) return;
    this.dotsContainer.innerHTML = '';
    for (let i = 0; i < this.slideCount; i++) {
      const dot = document.createElement('button');
      dot.className = 'slideshow-blaze__dot' + (i === 0 ? ' is-active' : '');
      dot.setAttribute('aria-label', 'Slide ' + (i + 1));
      dot.addEventListener('click', () => {
        this.goTo(i);
        this.pauseAutoplay();
      });
      this.dotsContainer.appendChild(dot);
    }
    this.dots = this.dotsContainer.querySelectorAll('.slideshow-blaze__dot');
  }

  buildArrows() {
    const arrowSvg = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>';

    const prevBtn = document.createElement('button');
    prevBtn.className = 'slideshow-blaze__arrow slideshow-blaze__arrow--prev';
    prevBtn.setAttribute('aria-label', 'Previous slide');
    prevBtn.innerHTML = arrowSvg;
    prevBtn.addEventListener('click', () => { this.prev(); this.pauseAutoplay(); });

    const nextBtn = document.createElement('button');
    nextBtn.className = 'slideshow-blaze__arrow slideshow-blaze__arrow--next';
    nextBtn.setAttribute('aria-label', 'Next slide');
    nextBtn.innerHTML = arrowSvg;
    nextBtn.addEventListener('click', () => { this.next(); this.pauseAutoplay(); });

    const slidesWrap = this.querySelector('.slideshow-blaze__slides');
    if (slidesWrap) {
      slidesWrap.appendChild(prevBtn);
      slidesWrap.appendChild(nextBtn);
    }
  }

  bindSwipe() {
    let startX = 0;
    let startY = 0;
    const slidesWrap = this.querySelector('.slideshow-blaze__slides');
    if (!slidesWrap) return;

    slidesWrap.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    }, { passive: true });

    slidesWrap.addEventListener('touchend', (e) => {
      const diffX = e.changedTouches[0].clientX - startX;
      const diffY = Math.abs(e.changedTouches[0].clientY - startY);
      if (Math.abs(diffX) > 50 && diffY < 100) {
        if (diffX < 0) this.next();
        else this.prev();
        this.pauseAutoplay();
      }
    }, { passive: true });
  }

  bindKeyboard() {
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') { this.prev(); this.pauseAutoplay(); }
      if (e.key === 'ArrowRight') { this.next(); this.pauseAutoplay(); }
    });
  }

  goTo(index, immediate) {
    if (index < 0) index = this.slideCount - 1;
    if (index >= this.slideCount) index = 0;

    const prevIndex = this.currentIndex;
    this.currentIndex = index;

    this.slides.forEach((slide, i) => {
      if (i === index) {
        slide.classList.add('is-active');
        slide.classList.remove('is-previous');
        slide.style.zIndex = '2';
      } else if (i === prevIndex && !immediate) {
        slide.classList.remove('is-active');
        slide.classList.add('is-previous');
        slide.style.zIndex = '1';
      } else {
        slide.classList.remove('is-active', 'is-previous');
        slide.style.zIndex = '0';
      }
    });

    this.boxes.forEach((box, i) => {
      if (i === index) {
        box.classList.add('is-active');
        box.classList.remove('is-previous');
      } else if (i === prevIndex && !immediate) {
        box.classList.remove('is-active');
        box.classList.add('is-previous');
      } else {
        box.classList.remove('is-active', 'is-previous');
      }
    });

    if (this.dots) {
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
      });
    }
  }

  next() { this.goTo(this.currentIndex + 1); }
  prev() { this.goTo(this.currentIndex - 1); }

  // --- Autoplay ---
  observeVisibility() {
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.startAutoplay();
        } else {
          this.stopAutoplay();
        }
      });
    }, { threshold: 0.3 });
    this.observer.observe(this);
  }

  startAutoplay() {
    if (this.autoplayTimer) return;
    const speed = parseInt(this.dataset.autorotateSpeed) || 5000;
    this.autoplayTimer = setInterval(() => this.next(), speed);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  pauseAutoplay() {
    this.stopAutoplay();
    // Resume after 10s of no interaction if autoplay was enabled
    if (this.isAutoplay) {
      clearTimeout(this.resumeTimer);
      this.resumeTimer = setTimeout(() => this.startAutoplay(), 10000);
    }
  }

  disconnectedCallback() {
    this.stopAutoplay();
    clearTimeout(this.resumeTimer);
    if (this.observer) this.observer.disconnect();
  }
}

customElements.define('slideshow-blaze', SlideshowBlaze);
